import { getConfig } from "config";
import { buildUrl } from "services/url";

export const CUSTOMER_SCAN_MAX_LENGTH = 4096;

/** Access codes remain on their established numeric path; scans stay strings. */
export const isTrackingBarcode = (scan: string) =>
  scan.length >= 8 &&
  scan.length <= CUSTOMER_SCAN_MAX_LENGTH &&
  /\S/.test(scan) &&
  !/^\d{1,10}$/.test(scan.trim());

type ScanLookup =
  | { status: "not_found" }
  | { status: "found"; reservation_id: string; can_deposit: boolean };

const request = async (path: string, signal: AbortSignal, scan?: string): Promise<unknown> => {
  const { apiUrl, apiKey, lockerId } = getConfig();
  const response = await fetch(buildUrl(apiUrl, path, { locker_group_id: lockerId }), {
    method: scan === undefined ? "GET" : "POST",
    cache: "no-store",
    signal,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Locker-Private-Key": apiKey,
    },
    body: scan === undefined ? undefined : JSON.stringify({ scan }),
  });
  if (!response.ok) throw new Error("Customer scan lookup unavailable");
  return response.json();
};

export const lookupCustomerScan = async (
  scan: string,
  signal: AbortSignal
): Promise<ScanLookup> => {
  const result = (await request("/v2/locker/reservations/scan-lookup/", signal, scan)) as {
    status?: string;
    reservation_id?: string;
    can_deposit?: boolean;
  };
  if (result?.status === "not_found") return { status: "not_found" };
  if (
    result?.status === "found" &&
    typeof result.reservation_id === "string" &&
    /^[A-Za-z0-9]+$/.test(result.reservation_id) &&
    typeof result.can_deposit === "boolean"
  ) {
    return result as Extract<ScanLookup, { status: "found" }>;
  }
  throw new Error("Invalid customer scan lookup response");
};

/** Keep only the identity and door addressing needed by the in-memory deposit controller. */
export const fetchCustomerScanReservation = async (id: string, signal: AbortSignal) => {
  const row = (await request(`/reservations/${encodeURIComponent(id)}/`, signal)) as Reservation;
  if (
    row?.id !== id ||
    typeof row.door?.id !== "string" ||
    !row.door.id.trim() ||
    typeof row.door.position !== "string" ||
    !row.door.position.trim() ||
    row.deposit_at ||
    row.pickup_at ||
    row.admin_pickup_at
  ) {
    throw new Error("Existing reservation is not eligible for deposit");
  }
  return { id: row.id, door: { id: row.door.id, position: row.door.position } };
};
